<script setup lang="ts">
import type { Name as ThemeName } from '#theme'
import type { UIDialogStepEntry } from '../../types'
import { computed, inject, useSlots, onMounted, onUnmounted, provide, useDefaults, useId, watch, UIDIALOG_KEY, UIDIALOG_SEQUENCE_KEY } from '#imports'

export interface UIDialogStepProps {
    /** Dedupe key: a second step with the same name is ignored while the first is queued. */
    name: string
    priority: number
    header?: string
    /** Small line above the header; a required step shows Required there instead. */
    kicker?: string
    /** Keeps the sheet at least this tall, for long content that would otherwise start as a sliver. */
    minHeight?: string
    /** Caps the sheet below its usual height, e.g. a compact signup whose terms scroll inside. */
    maxHeight?: string
    /** Theme for the sheet while this step shows, e.g. a light auth sheet on a dark site. */
    theme?: ThemeName
    /** Class on the sheet's backdrop while this step shows, e.g. the signup's own backdrop. */
    backdropClass?: string
    closeOnBackdropClick?: boolean
    /** Drops the sheet's body padding and lets the content fill the body, for edge-to-edge layouts. */
    flush?: boolean
    /** Marks the step as required: a Required label and lock note, which flashes on a backdrop tap; it never closes from one. */
    required?: boolean
    /** A wider sheet from tablet up, for long reading such as terms; left-aligned at every width. */
    wide?: boolean
    /** Keeps the heading at the left edge on a page without fields, which the sheet otherwise centres: the step's other pages ask for input. */
    headingStart?: boolean
    /** Draws the animated rim around the sheet, e.g. for a bonus offer. */
    highlight?: boolean
    /**
     * Opens on top of the step showing (e.g. terms from a link inside it) and hands the sheet back to
     * that step when it closes; the covered step keeps its state. Not counted in the progress.
     */
    interrupt?: boolean
    /** A request is in flight: the close icon is disabled and a backdrop tap does nothing. */
    busy?: boolean
    /** False for a step that isn't required but must be answered through its own buttons: no close icon. */
    closeIcon?: boolean
    /** False when the step closes itself after its close-icon-click handler, e.g. once a request it starts has succeeded. */
    closeOnCloseIconClick?: boolean
}

// withDefaults, like UIDialog: Vue casts absent boolean props to false, which useDefaults can't tell apart from an explicit false.
const _props = withDefaults(defineProps<UIDialogStepProps>(), {
    header: undefined,
    kicker: undefined,
    minHeight: undefined,
    maxHeight: undefined,
    theme: undefined,
    backdropClass: undefined,
    closeOnBackdropClick: true,
    flush: false,
    required: false,
    highlight: false,
    wide: false,
    headingStart: false,
    interrupt: false,
    busy: false,
    closeIcon: true,
    closeOnCloseIconClick: true
})

const props = useDefaults({
    props: _props
})

const emit = defineEmits<{
    'open': []
    'opened': []
    'close': []
    /** After close, once the sheet has animated out: the moment to unmount what the step shows. */
    'closed': []
    'backdrop-click': []
    'close-icon-click': []
}>()

defineSlots<{
    /** The very top of the sheet, above the progress row and heading. */
    top?(): any
    header?(props: { close: () => void }): any
    default(props: { close: () => void }): any
    footer?(props: { close: () => void }): any
    /** Covers the whole sheet, a shown close icon excepted, e.g. a loader while a request is in flight. */
    overlay?(): any
}>()

const slots = useSlots()

const modelValue = defineModel<boolean>({ default: false })

const id = useId()
const sequence = inject(UIDIALOG_SEQUENCE_KEY, null)
const isActive = computed(() => !!sequence?.isActive(id))
const isCovered = computed(() => !!sequence?.isCovered(id))
// Without a theme of its own a step takes the sheet's: the covered step's under an interrupt, else the site's.
const slotTheme = computed(() => props.theme ?? sequence?.theme.value)

function open() {
    modelValue.value = true
}

function close() {
    if (!modelValue.value) return

    modelValue.value = false
    // Closed, not dropped: it counts as done even when the parent pins the model and unmounts the step after.
    sequence?.unregister(id)
    emit('close')
}

function toggle() {
    if (modelValue.value) {
        return close()
    }

    open()
}

provide(UIDIALOG_KEY, { open, close, toggle })

// Like UIDialog's: a parent closes a step another step covers, e.g. signup when "Leave" answers its leave confirm.
defineExpose({ close })

const entry: UIDialogStepEntry = {
    id,
    get name() { return props.name },
    get priority() { return props.priority },
    get header() { return props.header },
    get kicker() { return props.kicker },
    get minHeight() { return props.minHeight },
    get maxHeight() { return props.maxHeight },
    get theme() { return props.theme },
    get backdropClass() { return props.backdropClass },
    get closeOnBackdropClick() { return props.closeOnBackdropClick },
    get flush() { return props.flush },
    get required() { return props.required },
    get highlight() { return props.highlight },
    get wide() { return props.wide },
    get headingStart() { return props.headingStart },
    get interrupt() { return props.interrupt },
    get busy() { return props.busy },
    get closeIcon() { return props.closeIcon },
    get closeOnCloseIconClick() { return props.closeOnCloseIconClick },
    get hasHeaderSlot() { return !!slots.header },
    get hasFooterSlot() { return !!slots.footer },
    close,
    backdropClick: () => emit('backdrop-click'),
    closeIconClick: () => emit('close-icon-click'),
    opened: () => emit('opened'),
    closed: () => emit('closed')
}

// Client-only: the host's teleport targets don't exist during SSR.
onMounted(() => {
    if (!sequence) return

    watch(modelValue, (value) => {
        if (!value) {
            sequence.unregister(id)

            return
        }

        if (sequence.register(entry)) {
            emit('open')
        }
    }, { immediate: true })
})

onUnmounted(() => {
    sequence?.unregister(id, { handoff: true })
})
</script>

<template>
    <!-- The slots teleport into the sheet but inject from here, so each gets the sheet's theme and its defaults again. -->
    <template v-if="sequence">
        <template v-if="isActive">
            <Teleport v-if="sequence.topEl.value && $slots.top" :to="sequence.topEl.value">
                <ThemeProvider :theme="slotTheme" class="dialog-step__slot">
                    <slot name="top" />
                </ThemeProvider>
            </Teleport>

            <Teleport v-if="sequence.headerEl.value && $slots.header" :to="sequence.headerEl.value">
                <ThemeProvider :theme="slotTheme" class="dialog-step__slot">
                    <slot name="header" :close />
                </ThemeProvider>
            </Teleport>

            <Teleport v-if="sequence.footerEl.value && $slots.footer" :to="sequence.footerEl.value">
                <ThemeProvider :theme="slotTheme" class="dialog-step__slot">
                    <slot name="footer" :close />
                </ThemeProvider>
            </Teleport>

            <Teleport v-if="sequence.overlayEl.value && $slots.overlay" :to="sequence.overlayEl.value">
                <ThemeProvider :theme="slotTheme" class="dialog-step__slot">
                    <slot name="overlay" />
                </ThemeProvider>
            </Teleport>
        </template>

        <!-- Stays rendered, only hidden, while an interrupting step covers it: a half-filled form survives. -->
        <Teleport v-if="sequence.bodyEl.value && (isActive || isCovered)" :to="sequence.bodyEl.value">
            <ThemeProvider v-show="isActive" :theme="slotTheme" class="dialog-step__body">
                <slot :close />
            </ThemeProvider>
        </Teleport>
    </template>

    <UIDialog
        v-else
        v-model="modelValue"
        :theme="props.theme"
        :header="props.header"
        :show-close-icon="false"
        :close-on-backdrop-click="props.closeOnBackdropClick && !props.required"
        @open="emit('open')"
        @opened="emit('opened')"
        @close="emit('close')"
        @closed="emit('closed')"
        @backdrop-click="emit('backdrop-click')"
    >
        <template v-if="$slots.header" #header>
            <slot name="header" :close />
        </template>

        <slot name="top" />

        <slot :close />

        <slot name="overlay" />

        <template v-if="$slots.footer" #footer>
            <slot name="footer" :close />
        </template>
    </UIDialog>
</template>

<style lang="scss" scoped>
.dialog-step__body,
.dialog-step__slot {
    display: contents;
}
</style>
